import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, finalize, map, takeUntil } from 'rxjs';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { DiscountService } from '../../../discount/services/discount.service';
import { FleetService } from '../../../fleet/services/fleet.service';
import { PackageService } from '../../../services/package.service';
import { Package, PackageDiscount } from '../../../shared/types';
import {
  AddOnApiPackage,
  NotificationPaymentModeApiItem,
  OrderRequestDiscountItem,
  OrderRequestFleetCatalogRow,
} from '../models/notification-order-request.models';
import { buildOrderRequestDiscountList } from '../utils/notification-order-request-discount.util';
import { filterActiveCarWashPackages } from '../utils/notification-order-request-package.util';
import {
  isValidAddOnPackagesCache,
  normalizeAddOnPackagesResponse,
} from '../../../shared/utils/add-on-packages-response.util';

/** Matches the discount list API's own `limit` ceiling. */
const ORDER_REQUEST_DISCOUNT_FETCH_LIMIT = 100;

@Injectable({ providedIn: 'root' })
export class NotificationOrderRequestCatalogService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly addonCacheByLocation: Record<string, AddOnApiPackage[]> = {};
  private readonly fleetCacheByLocationKey: Record<string, OrderRequestFleetCatalogRow[]> = {};
  private readonly packageCacheByLocation: Record<string, Package[]> = {};
  private readonly discountCacheByLocation: Record<string, OrderRequestDiscountItem[]> = {};
  private readonly paymentModeCacheByUserKey: Record<string, NotificationPaymentModeApiItem[]> = {};
  private addonLoadingLocationId: string | null = null;
  private fleetLoadingLocationKey: string | null = null;
  private packageLoadingLocationId: string | null = null;
  private discountLoadingLocationId: string | null = null;
  private paymentModeLoadingUserKey: string | null = null;
  private catalogRetryLocationId: string | null = null;
  /** Bumped on cache clear so in-flight package/add-on/discount responses cannot overwrite fresher data. */
  private packageLoadGeneration = 0;
  private addonLoadGeneration = 0;
  private discountLoadGeneration = 0;

  constructor(
    private readonly apiService: ApiService,
    private readonly authService: AuthService,
    private readonly discountService: DiscountService,
    private readonly fleetService: FleetService,
    private readonly packageService: PackageService
  ) {
    // Packages page CRUD clears PackageService caches; drop Order Request catalogs so reopen fetches fresh data.
    this.packageService.carWashPackagesCacheCleared$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.clearPackageCaches();
        this.clearAddonCaches();
      });
    // Discount page CRUD notifies DiscountService; drop Order Request discount catalog so reopen fetches fresh data.
    this.discountService.discountsCacheCleared$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.clearDiscountCaches();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fleetCatalogLocationKey(locationId?: string): string {
    const loc = String(locationId ?? '').trim();
    return loc || '__all__';
  }

  getCachedAddons(locationId: string): AddOnApiPackage[] | null {
    const loc = String(locationId || '').trim();
    if (!loc) {
      return null;
    }
    const cached = this.addonCacheByLocation[loc];
    if (!isValidAddOnPackagesCache(cached)) {
      return null;
    }
    return cached.map(a => ({ ...a }));
  }

  getCachedFleets(locationKey: string): OrderRequestFleetCatalogRow[] | null {
    const cached = this.fleetCacheByLocationKey[locationKey];
    if (!cached) {
      return null;
    }
    return cached.map(f => ({ ...f }));
  }

  isAddonLoadInFlight(locationId: string): boolean {
    return this.addonLoadingLocationId === String(locationId || '').trim();
  }

  isFleetLoadInFlight(locationKey: string): boolean {
    return this.fleetLoadingLocationKey === locationKey;
  }

  isPackageLoadInFlight(locationId: string): boolean {
    return this.packageLoadingLocationId === String(locationId || '').trim();
  }

  isDiscountLoadInFlight(locationId?: string): boolean {
    return this.discountLoadingLocationId === this.discountCacheLocationKey(locationId);
  }

  getCachedPackages(locationId: string): Package[] | null {
    const loc = String(locationId || '').trim();
    if (!loc) {
      return null;
    }
    const cached = this.packageCacheByLocation[loc];
    return cached ? cached.map(p => ({ ...p })) : null;
  }

  getCachedDiscounts(locationId?: string): OrderRequestDiscountItem[] | null {
    const cacheKey = this.discountCacheLocationKey(locationId);
    const cached = this.discountCacheByLocation[cacheKey];
    return cached ? cached.map(d => ({ ...d })) : null;
  }

  paymentModeCacheKey(): string {
    const userId = this.authService.user()?.id;
    return userId != null && String(userId).trim() ? String(userId).trim() : '__anonymous__';
  }

  getCachedPaymentModes(): NotificationPaymentModeApiItem[] | null {
    const cacheKey = this.paymentModeCacheKey();
    const cached = this.paymentModeCacheByUserKey[cacheKey];
    if (!this.isValidPaymentModesCache(cached)) {
      return null;
    }
    return cached.map(mode => ({
      ...mode,
      wash_types: [...mode.wash_types],
    }));
  }

  isPaymentModeLoadInFlight(): boolean {
    return this.paymentModeLoadingUserKey === this.paymentModeCacheKey();
  }

  private isValidPaymentModesCache(
    value: NotificationPaymentModeApiItem[] | undefined
  ): value is NotificationPaymentModeApiItem[] {
    return (
      Array.isArray(value) &&
      value.every(
        mode =>
          !!mode &&
          typeof mode.id === 'string' &&
          typeof mode.name === 'string' &&
          typeof mode.is_active === 'boolean' &&
          Array.isArray(mode.wash_types)
      )
    );
  }

  private discountCacheLocationKey(locationId?: string): string {
    const loc = String(locationId ?? '').trim();
    return loc || '__all__';
  }

  fetchPackagesByLocation(locationId: string): Observable<Package[]> {
    const loc = String(locationId || '').trim();
    return this.apiService.get<Package[]>(`package/view-by-location/${loc}`).pipe(
      map(response => {
        if (!response.status || !Array.isArray(response.data) || response.data.length === 0) {
          return [];
        }
        return filterActiveCarWashPackages(response.data);
      })
    );
  }

  /**
   * How many active discounts each location has, keyed by location id ('' when none was
   * given). Only two tiles are requested, so this is the only way the UI can tell whether
   * more exist; kept per location so a cache hit still has the right number.
   */
  private readonly discountTotalByLocation = new Map<string, number>();

  /** Total active discounts for a location, or null if it has not been loaded yet. */
  getDiscountTotalForLocation(locationId?: string): number | null {
    return this.discountTotalByLocation.get(String(locationId ?? '').trim()) ?? null;
  }

  fetchDiscountsByLocation(locationId?: string): Observable<OrderRequestDiscountItem[]> {
    const loc = String(locationId ?? '').trim();
    const discountOpts: {
      limit: number;
      is_active: boolean;
      type: 'normal';
      location_id?: string;
    } = {
      // The tiles are filtered by wash type in the UI, so the list has to arrive whole;
      // the old limit of 2 could hide every discount that applied to the wash being sold.
      limit: ORDER_REQUEST_DISCOUNT_FETCH_LIMIT,
      is_active: true,
      // Per-order custom discounts live in `discounts` with type=custom; never offer them as tiles.
      type: 'normal',
    };
    if (loc) {
      discountOpts.location_id = loc;
    }
    return this.packageService.getDiscounts(discountOpts).pipe(
      map(response => {
        const rows =
          response.status && Array.isArray(response.data)
            ? (response.data as PackageDiscount[])
            : [];

        // `pagination` is outside ApiResponse<T>; fall back to the row count so the
        // tooltip still reports something truthful if the envelope omits it.
        const pagination = (response as { pagination?: { total?: number } }).pagination;
        const total = Number(pagination?.total);
        this.discountTotalByLocation.set(loc, Number.isFinite(total) ? total : rows.length);

        return buildOrderRequestDiscountList(rows);
      })
    );
  }

  runPackageLoad(
    locationId: string,
    handlers: {
      onStart: () => void;
      onSuccess: (packages: Package[]) => void;
      onError: (err: any) => void;
      onFinalize: () => void;
    },
    options?: { force?: boolean }
  ): void {
    const loc = String(locationId || '').trim();
    if (!loc) {
      return;
    }
    if (this.packageLoadingLocationId === loc) {
      return;
    }

    const cached = this.packageCacheByLocation[loc];
    if (!options?.force && cached?.length) {
      handlers.onSuccess(cached.map(p => ({ ...p })));
      handlers.onFinalize();
      return;
    }

    this.packageLoadingLocationId = loc;
    const generation = this.packageLoadGeneration;
    handlers.onStart();
    this.fetchPackagesByLocation(loc)
      .pipe(
        finalize(() => {
          if (this.packageLoadGeneration !== generation) {
            return;
          }
          if (this.packageLoadingLocationId === loc) {
            this.packageLoadingLocationId = null;
          }
          handlers.onFinalize();
        })
      )
      .subscribe({
        next: packages => {
          if (this.packageLoadGeneration !== generation) {
            return;
          }
          this.packageCacheByLocation[loc] = packages.map(p => ({ ...p }));
          handlers.onSuccess(packages);
        },
        error: err => {
          if (this.packageLoadGeneration !== generation) {
            return;
          }
          handlers.onError(err);
        },
      });
  }

  runDiscountLoad(
    locationId: string | undefined,
    handlers: {
      onStart: () => void;
      onSuccess: (discounts: OrderRequestDiscountItem[]) => void;
      onError: (err: any) => void;
      onFinalize: () => void;
    },
    options?: { force?: boolean }
  ): void {
    const cacheKey = this.discountCacheLocationKey(locationId);
    if (this.discountLoadingLocationId === cacheKey) {
      return;
    }

    const cached = this.discountCacheByLocation[cacheKey];
    if (!options?.force && cached) {
      handlers.onSuccess(cached.map(d => ({ ...d })));
      handlers.onFinalize();
      return;
    }

    this.discountLoadingLocationId = cacheKey;
    const generation = this.discountLoadGeneration;
    handlers.onStart();
    this.fetchDiscountsByLocation(String(locationId ?? '').trim() || undefined)
      .pipe(
        finalize(() => {
          if (this.discountLoadGeneration !== generation) {
            return;
          }
          if (this.discountLoadingLocationId === cacheKey) {
            this.discountLoadingLocationId = null;
          }
          handlers.onFinalize();
        })
      )
      .subscribe({
        next: discounts => {
          if (this.discountLoadGeneration !== generation) {
            return;
          }
          this.discountCacheByLocation[cacheKey] = discounts.map(d => ({ ...d }));
          handlers.onSuccess(discounts);
        },
        error: err => {
          if (this.discountLoadGeneration !== generation) {
            return;
          }
          handlers.onError(err);
        },
      });
  }

  scheduleCatalogRetry(locationId: string): void {
    this.catalogRetryLocationId = String(locationId || '').trim() || null;
  }

  takeCatalogRetryLocationId(): string | null {
    const loc = this.catalogRetryLocationId;
    this.catalogRetryLocationId = null;
    return loc;
  }

  fetchAddonsByLocation(locationId: string): Observable<AddOnApiPackage[]> {
    const loc = String(locationId || '').trim();
    return this.apiService
      .get<AddOnApiPackage | AddOnApiPackage[]>(`package/add-ons-by-location/${loc}`)
      .pipe(
        map(response => {
          if (!response.status || response.data == null) {
            return [];
          }
          return normalizeAddOnPackagesResponse(response.data) as AddOnApiPackage[];
        })
      );
  }

  fetchFleets(locationId?: string): Observable<OrderRequestFleetCatalogRow[]> {
    const loc = String(locationId ?? '').trim();
    const fleetReq = loc ? { location_id: loc } : {};
    return this.fleetService.queryFleets(fleetReq).pipe(
      map(({ fleets }) =>
        fleets.map(f => ({
          id: f.id,
          label: f.clientName,
        }))
      )
    );
  }

  runAddonLoad(
    locationId: string,
    handlers: {
      onStart: () => void;
      onSuccess: (raw: AddOnApiPackage[]) => void;
      onError: (err: any) => void;
      onFinalize: () => void;
    },
    options?: { force?: boolean }
  ): void {
    const loc = String(locationId || '').trim();
    if (!loc) {
      return;
    }
    if (this.addonLoadingLocationId === loc) {
      this.scheduleCatalogRetry(loc);
      return;
    }

    const cached = this.addonCacheByLocation[loc];
    if (!options?.force && isValidAddOnPackagesCache(cached)) {
      handlers.onSuccess(cached.map(a => ({ ...a })));
      handlers.onFinalize();
      return;
    }

    this.addonLoadingLocationId = loc;
    const generation = this.addonLoadGeneration;
    handlers.onStart();
    this.fetchAddonsByLocation(loc)
      .pipe(
        finalize(() => {
          if (this.addonLoadGeneration !== generation) {
            return;
          }
          if (this.addonLoadingLocationId === loc) {
            this.addonLoadingLocationId = null;
          }
          handlers.onFinalize();
        })
      )
      .subscribe({
        next: raw => {
          if (this.addonLoadGeneration !== generation) {
            return;
          }
          if (isValidAddOnPackagesCache(raw)) {
            this.addonCacheByLocation[loc] = raw.map(a => ({ ...a }));
          } else {
            delete this.addonCacheByLocation[loc];
          }
          handlers.onSuccess(raw);
        },
        error: err => {
          if (this.addonLoadGeneration !== generation) {
            return;
          }
          handlers.onError(err);
        },
      });
  }

  runFleetLoad(
    locationId: string | undefined,
    handlers: {
      onStart: () => void;
      onSuccess: (rows: OrderRequestFleetCatalogRow[], locationKey: string) => void;
      onError: (err: any) => void;
      onFinalize: () => void;
    },
    options?: { force?: boolean }
  ): void {
    const loc = String(locationId ?? '').trim();
    const locationKey = this.fleetCatalogLocationKey(loc);
    if (this.fleetLoadingLocationKey === locationKey) {
      this.scheduleCatalogRetry(loc);
      return;
    }

    const cached = this.fleetCacheByLocationKey[locationKey];
    if (!options?.force && cached) {
      handlers.onSuccess(
        cached.map(f => ({ ...f })),
        locationKey
      );
      handlers.onFinalize();
      return;
    }

    this.fleetLoadingLocationKey = locationKey;
    handlers.onStart();
    this.fetchFleets(loc)
      .pipe(
        finalize(() => {
          if (this.fleetLoadingLocationKey === locationKey) {
            this.fleetLoadingLocationKey = null;
          }
          handlers.onFinalize();
        })
      )
      .subscribe({
        next: rows => {
          this.fleetCacheByLocationKey[locationKey] = rows.map(f => ({ ...f }));
          handlers.onSuccess(rows, locationKey);
        },
        error: err => handlers.onError(err),
      });
  }

  fetchPaymentModes(): Observable<NotificationPaymentModeApiItem[]> {
    return this.apiService.get<NotificationPaymentModeApiItem[]>('user/payment-modes').pipe(
      map(response => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        return rows
          .filter(mode => !!mode?.is_active)
          .map(mode => ({
            id: String(mode.id ?? ''),
            name: String(mode.name ?? ''),
            is_active: true,
            wash_types: Array.isArray(mode.wash_types)
              ? mode.wash_types.map(washType => String(washType))
              : [],
          }));
      })
    );
  }

  runPaymentModeLoad(
    handlers: {
      onStart: () => void;
      onSuccess: (modes: NotificationPaymentModeApiItem[]) => void;
      onError: (err: any) => void;
      onFinalize: () => void;
    },
    options?: { force?: boolean }
  ): void {
    const cacheKey = this.paymentModeCacheKey();
    if (this.paymentModeLoadingUserKey === cacheKey) {
      return;
    }

    const cached = this.paymentModeCacheByUserKey[cacheKey];
    if (!options?.force && this.isValidPaymentModesCache(cached)) {
      handlers.onSuccess(
        cached.map(mode => ({
          ...mode,
          wash_types: [...mode.wash_types],
        }))
      );
      handlers.onFinalize();
      return;
    }

    this.paymentModeLoadingUserKey = cacheKey;
    handlers.onStart();
    this.fetchPaymentModes()
      .pipe(
        finalize(() => {
          if (this.paymentModeLoadingUserKey === cacheKey) {
            this.paymentModeLoadingUserKey = null;
          }
          handlers.onFinalize();
        })
      )
      .subscribe({
        next: modes => {
          this.paymentModeCacheByUserKey[cacheKey] = modes.map(mode => ({
            ...mode,
            wash_types: [...mode.wash_types],
          }));
          handlers.onSuccess(modes);
        },
        error: err => handlers.onError(err),
      });
  }

  /** Drop location-scoped wash package catalog cache (e.g. after package create/update/delete). */
  clearPackageCaches(): void {
    Object.keys(this.packageCacheByLocation).forEach(k => delete this.packageCacheByLocation[k]);
    this.packageLoadingLocationId = null;
    this.packageLoadGeneration += 1;
  }

  /** Drop location-scoped add-on catalog cache (e.g. after add-on package create/update/delete). */
  clearAddonCaches(): void {
    Object.keys(this.addonCacheByLocation).forEach(k => delete this.addonCacheByLocation[k]);
    this.addonLoadingLocationId = null;
    this.addonLoadGeneration += 1;
  }

  /**
   * Persist an Order Request custom discount as `discounts.type = custom` so the order can
   * store `orders.discount_id`. Does not refresh shop-discount tiles (see DiscountService).
   */
  createCustomDiscountForOrder(input: {
    name: string;
    discountType: 'flat' | 'percentage';
    discountValue: number;
    reason?: string;
  }): Observable<string> {
    const reason = String(input.reason ?? '').trim();
    return this.discountService
      .createDiscount({
        name: input.name,
        discount_type: input.discountType,
        discount_value: input.discountValue,
        type: 'custom',
        ...(reason ? { reason } : {}),
      })
      .pipe(
        map(response => {
          const id = String(response?.data?.id ?? '').trim();
          if (!id) {
            throw new Error('Custom discount was created without an id');
          }
          return id;
        })
      );
  }

  /** Drop location-scoped discount catalog cache (e.g. after discount create/update/delete). */
  clearDiscountCaches(): void {
    Object.keys(this.discountCacheByLocation).forEach(k => delete this.discountCacheByLocation[k]);
    this.discountTotalByLocation.clear();
    this.discountLoadingLocationId = null;
    this.discountLoadGeneration += 1;
  }

  clearAll(): void {
    this.clearAddonCaches();
    Object.keys(this.fleetCacheByLocationKey).forEach(k => delete this.fleetCacheByLocationKey[k]);
    this.clearPackageCaches();
    this.clearDiscountCaches();
    Object.keys(this.paymentModeCacheByUserKey).forEach(
      k => delete this.paymentModeCacheByUserKey[k]
    );
    this.fleetLoadingLocationKey = null;
    this.paymentModeLoadingUserKey = null;
    this.catalogRetryLocationId = null;
  }
}
